import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * StarFilled icon component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconStarFilled extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12.8385 2.07434L15.5026 7.45265C15.5631 7.58916 15.6585 7.70741 15.779 7.7956C15.8995 7.8838 16.0411 7.93886 16.1895 7.9553L22.0705 8.82655C22.2407 8.84844 22.4013 8.91832 22.5333 9.02804C22.6654 9.13776 22.7635 9.28278 22.8162 9.44617C22.8689 9.60956 22.874 9.78457 22.831 9.95077C22.7879 10.117 22.6985 10.2675 22.5731 10.3848L18.3341 14.5902C18.2259 14.6913 18.1447 14.8178 18.0979 14.9583C18.0511 15.0988 18.0401 15.2487 18.066 15.3945L19.0881 21.3089C19.1177 21.4789 19.099 21.6537 19.0341 21.8136C18.9692 21.9734 18.8608 22.1118 18.7211 22.2131C18.5815 22.3143 18.4162 22.3744 18.2441 22.3863C18.072 22.3983 17.9 22.3617 17.7477 22.2807L12.4532 19.4827C12.3176 19.4161 12.1686 19.3815 12.0175 19.3815C11.8665 19.3815 11.7175 19.4161 11.5819 19.4827L6.28737 22.2807C6.13505 22.3617 5.96306 22.3983 5.79097 22.3863C5.61888 22.3744 5.4536 22.3143 5.31394 22.2131C5.17428 22.1118 5.06584 21.9734 5.00097 21.8136C4.93609 21.6537 4.91739 21.4789 4.94698 21.3089L5.96903 15.3274C5.99493 15.1817 5.98397 15.0317 5.93715 14.8913C5.89034 14.7508 5.80914 14.6243 5.70095 14.5232L1.4117 10.3848C1.28481 10.2643 1.19557 10.1096 1.15479 9.93944C1.11402 9.76928 1.12346 9.59094 1.18197 9.42604C1.24049 9.26114 1.34556 9.11674 1.48447 9.01034C1.62338 8.90394 1.79016 8.8401 1.96461 8.82655L7.84558 7.9553C7.99402 7.93886 8.13556 7.8838 8.25609 7.7956C8.37662 7.70741 8.47193 7.58916 8.53252 7.45265L11.1965 2.07434C11.2691 1.9177 11.3849 1.78508 11.5304 1.69213C11.6759 1.59919 11.8449 1.5498 12.0175 1.5498C12.1902 1.5498 12.3592 1.59919 12.5047 1.69213C12.6501 1.78508 12.766 1.9177 12.8385 2.07434Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-star-filled') === undefined) {
  customElements.define('cat-icon-star-filled', BlocksIconStarFilled);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-star-filled': BlocksIconStarFilled;
  }
}
